import ProductInterface from "../../Interfaces/ProductInterface";
import { Field, useFormikContext } from "formik";
import { useState, ChangeEvent, useEffect } from "react";
import VariantInterface from "../../Interfaces/VariantInterface";
import { sectionSettings, translations, formatMoney, getSrcSet, getImageSizes } from "../../Helpers/WindowHelpers";
import { changeSelectedVariant, updateVariants } from "../../Helpers/FormikUpdateFunctions";
import Modal from "./ModalSection";
import VariantModalBodyDisplay from "../Displays/VariantModalBodyDisplay";
interface ProductInputInterface {
    classes?: string,
    name: string,
    product: ProductInterface,
    step: number
}

const ProductInput = ({ classes, name, product, step }: ProductInputInterface) => {
    const context = useFormikContext<any>();
    const initialValue = context.values?.variants?.find((item: any) => item.productId == product.id)
    const [selectedVariant, setSelectedVariant] = useState<VariantInterface>(initialValue ? product.variants.find((item) => item.id == initialValue.variantId) : product.first_available_variant);
    const [autoSelected, setAutoSelected] = useState<boolean>(false);

    const updateVariant = (value: string) => {
        const variant = product.variants.find((item) => item.id == value);
        setSelectedVariant(variant);

        //if(context.values?.variants?.some((item: any) => item.productId == variant.product))
        changeSelectedVariant(name, variant, context, step, getFullName(variant));
    }

    const getFullName = (variant: VariantInterface) => {
        if (product.variants_count > 1) {
            return `${product.title}: ${variant.title}`;
        }
        return product.title;
    }

    const onChange = (value: string) => {
        const variant = product.variants.find((item) => item.id == value);
        updateVariants(name, variant, context, step, getFullName(variant));
    }

    const isInForm = !!context?.values.variants?.some((item: any) => item.productId == product.id);
    const isChecked = !!autoSelected || isInForm;
    const isFeatured = product.metafields.custom.featured_upgrade == 'true';
    const isDisabled = (autoSelected || !selectedVariant.available);

    useEffect(() => {
        setSelectedVariant(initialValue ? product.variants.find((item) => item.id == initialValue.variantId) : product.first_available_variant);
    }, [product]);

    useEffect(() => {
        const variants = context.values?.variants ?? [];
        const preSelectedVariant = product.variants.find((variant: VariantInterface) =>
            variants.find((item: any) => {
                const selectedVariantAssociatedVariants = selectedVariant.metafields.custom.associated_variants || [];
                return item.associatedVariants?.find((variantId: string) =>
                    variantId.toString() == variant.id
                ) || (selectedVariantAssociatedVariants.length > 0 && selectedVariantAssociatedVariants.every((variantId) => item.associatedVariants?.includes(variantId.toString())));
            }));
        
        if(preSelectedVariant) {      
            setSelectedVariant(preSelectedVariant);
            setAutoSelected(true);
            return;
        }
        setAutoSelected(false);
    }, [context?.values?.variants])

    return (
        <div className="grid__item md--33 width--50 mb-20">
            <div className={`promo-cards__card ${parseFloat(selectedVariant.price) > 0 ? 'is-upsell' : ''} ${isDisabled ? 'disabled' : ''} ${isChecked ? 'checked' : ''} ${isFeatured ? 'featured-upgrade' : ''}`}>
                <div className="promo-cards__top card-element">
                    <img
                        className="promo-cards__image d--block"
                        srcSet={getSrcSet(selectedVariant.featured_image?.src ?? product.featured_image.src, [200, 300, 400, 600])}
                        sizes={getImageSizes('300px', [
                            {
                                screen: 767,
                                view: '48vw'
                            },
                            {
                                screen: 1099,
                                view: '30vw'
                            },
                            {
                                screen: 1664,
                                view: '300px'
                            }
                        ])}
                        alt={product.title}
                        loading="lazy"
                        tabIndex={-1}
                        aria-hidden="true"
                    />
                    {selectedVariant.metafields?.custom?.promotion_badge?.name && (
                        <div className="promo-cards__badge promotion-badge">{selectedVariant.metafields.custom.promotion_badge.name}</div>
                    )}
                </div>
                <div className="promo-cards__bottom flex-grow gap-16">
                    <Field
                        className="card-product__input visually-hidden"
                        type="checkbox"
                        name={name}
                        id={product.id}
                        value={selectedVariant.id}
                        checked={isChecked}
                        disabled={autoSelected || !selectedVariant.available}
                        onChange={(e: ChangeEvent) => onChange((e.currentTarget as HTMLInputElement).value)}
                    />
                    <label tabIndex={0} className="h5 d--flex gap-4 card-element" htmlFor={`${product.id}`}>
                        <span className="promo-cards__indicator is-checkbox"></span>
                        <div className="d--flex gap-4 flex-wrap--wrap">
                            {product.title}
                            {parseFloat(selectedVariant.price) <= 0 && <div className="info-badge">{sectionSettings.label_free}</div>}
                        </div>
                    </label>
                    {(product.variants_count > 1 && product.variants.some((variant) => variant.available)) && (
                        <div className="card-element">
                            <label
                                htmlFor={`${product.id}_select`}
                                className="d--flex text--md text--bold option-name">
                                {product.options_with_values[0].name}
                            </label>
                            <select
                                id={`${product.id}_select`}
                                className="promo-cards__select text--sm"
                                value={selectedVariant.id}
                                onChange={(e: ChangeEvent) => updateVariant((e.currentTarget as HTMLSelectElement).value)}
                            >
                                {product.variants.filter((variant) => variant.available).map((variant, index) => (
                                    <option key={index} value={variant.id}>{variant.title}</option>
                                ))}
                            </select>
                        </div>
                    )}
                    <p className="promo-cards__desc card-element">{selectedVariant.metafields?.custom?.blurb}</p>
                    {selectedVariant.metafields?.custom?.tagline && (
                        <p className="card-product__subtitle card-element color--gray-muted text--sm text--uppercase mb-10">
                            {selectedVariant.metafields.custom.tagline}
                        </p>
                    )}

                    <div className="d--flex xs--flex-column gap-8 justify--space-between mt-auto">
                        {parseFloat(selectedVariant.price) > 0 &&
                            <div className="promo-cards__price d--flex gap-4 card-element">
                                <span className="price">
                                    +{formatMoney(selectedVariant.price)}
                                </span>
                                {parseFloat(selectedVariant.compare_at_price) > parseFloat(selectedVariant.price) && (
                                    <s className="price--compare color--gray-muted ml-5">
                                        {formatMoney(selectedVariant.compare_at_price)}
                                    </s>
                                )}
                            </div>
                        }
                        <Modal
                            classes={"link card-element"}
                            buttonBody={translations.details}
                        >
                            <VariantModalBodyDisplay
                                fieldName={name}
                                variant={selectedVariant}
                                product={product}
                                disableAdd={isDisabled}
                                image={selectedVariant.featured_image.src ? selectedVariant.featured_image : product.featured_image}
                                multiselect={true}
                                step={step}
                            >
                            </VariantModalBodyDisplay>
                        </Modal>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default ProductInput